import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { AIBox } from '@crazyegginc/hatch';

import { useAuthContext, useCrazyAI, useUserLanguage } from '/src/hooks';
import { AI_PROMPTS } from '../../_global/constants';
import { useSelectedSource } from '/src/components/source-selector/source-selector';

export function AISummary({ analyticsData, enabled, loading }) {
  const location = useLocation();
  const { currentUser } = useAuthContext();
  const userPreferredLanguage = useUserLanguage();
  const { selectedSource } = useSelectedSource();

  const params = useMemo(
    () => ({
      data: JSON.stringify(analyticsData),
      language: userPreferredLanguage,
      sourceName: selectedSource?.name,
      sourceType: selectedSource?.type?.toLowerCase(),
    }),
    [analyticsData, userPreferredLanguage, selectedSource?.name, selectedSource?.type],
  );

  const context = useMemo(
    () => ({
      url: `${location.pathname}${location.search}`,
      userId: currentUser?.userId,
    }),
    [location.pathname, location.search, currentUser?.userId],
  );

  const {
    data,
    error: aiError,
    refetch,
    positiveFeedback,
    negativeFeedback,
  } = useCrazyAI({
    type: AI_PROMPTS.WEB_ANALYTICS_SUMMARY,
    params,
    context,
    enabled: enabled && !loading,
  });

  const { insights, error } = useMemo(() => {
    let insights;
    try {
      if (data?.ready) {
        insights = JSON.parse(data?.text).insights;
        if (insights.length < 1) {
          return { error: true };
        } else {
          return { insights };
        }
      }
    } catch {
      return { error: true };
    }
    return {};
  }, [data]);

  if (!enabled) return null;

  return (
    <AIBox
      size="lg"
      border={true}
      id="wa-summary"
      error={error || aiError}
      loading={!data?.ready}
      selectedFeedback={data?.feedback}
      refetch={refetch}
      onPositive={positiveFeedback}
      onNegative={negativeFeedback}
      showChangeLanguage={true}
    >
      <>
        <div className="text-header-2 mb-1">Top Insights</div>
        {insights?.map((i) => (
          <div key={i.header}>
            <div className="text-body-1">{i.header}</div>
            <ul className="ml-5 list-outside list-disc mb-2">
              <li className="text-body-2">{i.detail}</li>
              <li className="text-body-2">{i.action}</li>
            </ul>
          </div>
        ))}
      </>
    </AIBox>
  );
}
